import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  Calendar, Clock, User, Phone, Mail, CheckCircle, XCircle, 
  Eye, Trash2, Plus, Monitor, MapPin, Ban, RefreshCw, Link2, Unlink
} from 'lucide-react';
import { format, parseISO, isToday, isThisWeek, addDays, startOfDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';

interface Appointment {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  customer_message: string | null;
  appointment_type: 'remote' | 'onsite';
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  admin_notes: string | null;
  google_event_id: string | null;
  created_at: string;
}

interface BlockedSlot {
  id: string;
  block_date: string;
  start_time: string;
  end_time: string;
  reason: string | null;
  google_event_id: string | null;
  is_from_google: boolean | null;
}

export function AppointmentManagement() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [blockedSlots, setBlockedSlots] = useState<BlockedSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState<{ connected: boolean; loading: boolean; calendar_id?: string }>({ connected: false, loading: true });
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [calendars, setCalendars] = useState<{ id: string; summary: string; primary: boolean }[]>([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>('');
  const { toast } = useToast();

  // Form states for new appointment
  const [newAppointment, setNewAppointment] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_message: '',
    appointment_type: 'remote' as 'remote' | 'onsite',
    appointment_date: undefined as Date | undefined,
    start_time: '',
    end_time: '',
  });

  // Form states for blocking slots
  const [blockForm, setBlockForm] = useState({
    block_date: undefined as Date | undefined,
    start_time: '',
    end_time: '',
    reason: '',
  });

  useEffect(() => {
    loadData();
    checkGoogleCalendarStatus();
  }, []);

  const checkGoogleCalendarStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: { action: 'get_status' },
      });

      if (error) throw error;
      setGoogleCalendarStatus({ 
        connected: data?.connected || false, 
        loading: false,
        calendar_id: data?.calendar_id 
      });
      if (data?.calendar_id) {
        setSelectedCalendarId(data.calendar_id);
      }
      if (data?.connected) {
        loadCalendarList();
      }
    } catch (error) {
      console.error('Error checking Google Calendar status:', error);
      setGoogleCalendarStatus({ connected: false, loading: false });
    }
  };

  const loadCalendarList = async () => {
    setLoadingCalendars(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: { action: 'list_calendars' },
      });

      if (error) throw error;
      setCalendars(data?.calendars || []);
    } catch (error) {
      console.error('Error loading calendars:', error);
    } finally {
      setLoadingCalendars(false);
    }
  };

  const updateSelectedCalendar = async (calendarId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: { action: 'set_calendar', calendarId },
      });

      if (error) throw error;
      
      setSelectedCalendarId(calendarId);
      setGoogleCalendarStatus(prev => ({ ...prev, calendar_id: calendarId }));
      
      const calendarName = calendars.find(c => c.id === calendarId)?.summary || calendarId;
      toast({
        title: 'Kalender aktualisiert',
        description: `"${calendarName}" wird jetzt für Termine verwendet.`,
      });
    } catch (error) {
      console.error('Error updating calendar:', error);
      toast({
        title: 'Fehler',
        description: 'Kalender konnte nicht aktualisiert werden.',
        variant: 'destructive',
      });
    }
  };

  const connectGoogleCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-auth');
      
      if (error) throw error;
      
      if (data?.auth_url) {
        const popup = window.open(data.auth_url, 'Google Calendar Auth', 'width=600,height=700');
        
        // Listen for the popup message
        const handleMessage = (event: MessageEvent) => {
          if (event.data?.type === 'GOOGLE_CALENDAR_CONNECTED') {
            window.removeEventListener('message', handleMessage);
            setGoogleCalendarStatus({ connected: true, loading: false });
            toast({
              title: 'Google Calendar verbunden',
              description: 'Ihre Termine werden jetzt synchronisiert.',
            });
          }
        };
        window.addEventListener('message', handleMessage);
        
        // Fallback: Check status after popup closes
        const checkPopup = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkPopup);
            setTimeout(() => {
              checkGoogleCalendarStatus();
            }, 1000);
          }
        }, 500);
      }
    } catch (error: any) {
      console.error('Error connecting Google Calendar:', error);
      toast({
        title: 'Fehler',
        description: 'Google Calendar konnte nicht verbunden werden.',
        variant: 'destructive',
      });
    } finally {
      setConnectingCalendar(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [aptResult, blockedResult] = await Promise.all([
        supabase
          .from('appointments')
          .select('*')
          .order('appointment_date', { ascending: true })
          .order('start_time', { ascending: true }),
        supabase
          .from('blocked_slots')
          .select('*')
          .order('block_date', { ascending: true })
      ]);

      if (aptResult.error) throw aptResult.error;
      if (blockedResult.error) throw blockedResult.error;

      setAppointments(aptResult.data || []);
      setBlockedSlots(blockedResult.data || []);
    } catch (error: any) {
      console.error('Error loading appointments:', error);
      toast({
        title: 'Fehler',
        description: 'Termine konnten nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: 'confirmed' | 'cancelled' | 'completed') => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      setAppointments(prev => prev.map(apt => 
        apt.id === id ? { ...apt, status } : apt
      ));

      // Send notification email
      const appointment = appointments.find(a => a.id === id);
      if (appointment) {
        try {
          await supabase.functions.invoke('send-appointment-notification', {
            body: {
              type: status === 'confirmed' ? 'confirmed' : status === 'cancelled' ? 'cancelled' : 'confirmed',
              customer_email: appointment.customer_email,
              customer_name: appointment.customer_name,
              appointment_date: appointment.appointment_date,
              start_time: appointment.start_time,
              end_time: appointment.end_time,
              appointment_type: appointment.appointment_type,
            },
          });
        } catch (emailError) {
          console.error('Failed to send notification:', emailError);
        }
      }

      toast({
        title: 'Status aktualisiert',
        description: `Termin wurde auf "${status}" gesetzt.`,
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const deleteAppointment = async (id: string) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setAppointments(prev => prev.filter(apt => apt.id !== id));
      toast({
        title: 'Termin gelöscht',
        description: 'Der Termin wurde erfolgreich entfernt.',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const createAppointment = async () => {
    if (!newAppointment.appointment_date || !newAppointment.start_time || !newAppointment.end_time) {
      toast({
        title: 'Fehler',
        description: 'Bitte Datum und Uhrzeit ausfüllen.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('appointments')
        .insert([{
          customer_name: newAppointment.customer_name,
          customer_email: newAppointment.customer_email,
          customer_phone: newAppointment.customer_phone || null,
          customer_message: newAppointment.customer_message || null,
          appointment_type: newAppointment.appointment_type,
          appointment_date: format(newAppointment.appointment_date, 'yyyy-MM-dd'),
          start_time: newAppointment.start_time,
          end_time: newAppointment.end_time,
          status: 'confirmed',
        }]);

      if (error) throw error;

      toast({
        title: 'Termin erstellt',
        description: 'Der Termin wurde erfolgreich hinzugefügt.',
      });
      setShowAddDialog(false);
      setNewAppointment({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_message: '',
        appointment_type: 'remote',
        appointment_date: undefined,
        start_time: '',
        end_time: '',
      });
      loadData();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const createBlockedSlot = async () => {
    if (!blockForm.block_date || !blockForm.start_time || !blockForm.end_time) {
      toast({
        title: 'Fehler',
        description: 'Bitte Datum und Uhrzeit ausfüllen.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('blocked_slots')
        .insert([{
          block_date: format(blockForm.block_date, 'yyyy-MM-dd'),
          start_time: blockForm.start_time,
          end_time: blockForm.end_time,
          reason: blockForm.reason || null,
        }]);

      if (error) throw error;

      toast({
        title: 'Zeitslot blockiert',
        description: 'Der Zeitslot wurde erfolgreich blockiert.',
      });
      setShowBlockDialog(false);
      setBlockForm({
        block_date: undefined,
        start_time: '',
        end_time: '',
        reason: '',
      });
      loadData();
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const deleteBlockedSlot = async (slot: BlockedSlot) => {
    try {
      // If this is a Google Calendar blocked slot, delete from Google Calendar first
      if (slot.is_from_google && slot.google_event_id) {
        try {
          await supabase.functions.invoke('google-calendar-sync', {
            body: { 
              action: 'delete_blocked_time',
              googleEventId: slot.google_event_id 
            },
          });
        } catch (googleError) {
          console.error('Error deleting from Google Calendar:', googleError);
          // Continue with local deletion even if Google deletion fails
        }
      }

      const { error } = await supabase
        .from('blocked_slots')
        .delete()
        .eq('id', slot.id);

      if (error) throw error;

      setBlockedSlots(prev => prev.filter(s => s.id !== slot.id));
      toast({
        title: 'Blockierung entfernt',
        description: slot.is_from_google 
          ? 'Der Zeitslot wurde aus dem Kalender und der Datenbank entfernt.'
          : 'Der Zeitslot ist wieder verfügbar.',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const updateAdminNotes = async (id: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ admin_notes: notes })
        .eq('id', id);

      if (error) throw error;

      setAppointments(prev => prev.map(apt => 
        apt.id === id ? { ...apt, admin_notes: notes } : apt
      ));

      toast({
        title: 'Notizen gespeichert',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Bestätigt</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Ausstehend</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Storniert</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-800"><CheckCircle className="h-3 w-3 mr-1" />Abgeschlossen</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const stats = {
    total: appointments.length,
    pending: appointments.filter(a => a.status === 'pending').length,
    today: appointments.filter(a => {
      try {
        return isToday(parseISO(a.appointment_date));
      } catch {
        return false;
      }
    }).length,
    thisWeek: appointments.filter(a => {
      try {
        return isThisWeek(parseISO(a.appointment_date), { weekStartsOn: 1 });
      } catch {
        return false;
      }
    }).length,
  };

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', 
    '15:00', '16:00', '17:00', '17:30', '18:00', '19:00', '20:00', '20:30'
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Gesamt</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700">Ausstehend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Heute</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.today}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Diese Woche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.thisWeek}</div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-cyan-600 hover:bg-cyan-700">
              <Plus className="h-4 w-4 mr-2" />
              Termin hinzufügen
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Neuen Termin erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Name *</label>
                  <Input
                    value={newAppointment.customer_name}
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, customer_name: e.target.value }))}
                    placeholder="Kundenname"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">E-Mail *</label>
                  <Input
                    type="email"
                    value={newAppointment.customer_email}
                    onChange={(e) => setNewAppointment(prev => ({ ...prev, customer_email: e.target.value }))}
                    placeholder="kunde@email.de"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Telefon *</label>
                <Input
                  type="tel"
                  value={newAppointment.customer_phone}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="Telefonnummer"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Art</label>
                <Select
                  value={newAppointment.appointment_type}
                  onValueChange={(value: 'remote' | 'onsite') => 
                    setNewAppointment(prev => ({ ...prev, appointment_type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remote">
                      <span className="flex items-center gap-2">
                        <Monitor className="h-4 w-4" /> Remote
                      </span>
                    </SelectItem>
                    <SelectItem value="onsite">
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" /> Vor Ort
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Datum *</label>
                <CalendarComponent
                  mode="single"
                  selected={newAppointment.appointment_date}
                  onSelect={(date) => setNewAppointment(prev => ({ ...prev, appointment_date: date }))}
                  disabled={(date) => date < startOfDay(new Date())}
                  locale={de}
                  className="rounded-md border mt-1"
                  classNames={{}}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Startzeit *</label>
                  <Select
                    value={newAppointment.start_time}
                    onValueChange={(value) => setNewAppointment(prev => ({ ...prev, start_time: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Startzeit" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Endzeit *</label>
                  <Select
                    value={newAppointment.end_time}
                    onValueChange={(value) => setNewAppointment(prev => ({ ...prev, end_time: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Endzeit" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Nachricht / Notizen</label>
                <Textarea
                  value={newAppointment.customer_message}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, customer_message: e.target.value }))}
                  placeholder="Optionale Notizen..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button variant="outline">Abbrechen</Button>
                </DialogClose>
                <Button onClick={createAppointment} className="bg-cyan-600 hover:bg-cyan-700">
                  Termin erstellen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
          <DialogTrigger asChild>
            <Button variant="outline">
              <Ban className="h-4 w-4 mr-2" />
              Zeitslot blockieren
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Zeitslot blockieren</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Datum *</label>
                <CalendarComponent
                  mode="single"
                  selected={blockForm.block_date}
                  onSelect={(date) => setBlockForm(prev => ({ ...prev, block_date: date }))}
                  disabled={(date) => date < startOfDay(new Date())}
                  locale={de}
                  className="rounded-md border mt-1"
                  classNames={{}}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Von *</label>
                  <Select
                    value={blockForm.start_time}
                    onValueChange={(value) => setBlockForm(prev => ({ ...prev, start_time: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Startzeit" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Bis *</label>
                  <Select
                    value={blockForm.end_time}
                    onValueChange={(value) => setBlockForm(prev => ({ ...prev, end_time: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Endzeit" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Grund (optional)</label>
                <Input
                  value={blockForm.reason}
                  onChange={(e) => setBlockForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="z.B. Privater Termin"
                />
              </div>
              <div className="flex justify-end gap-2">
                <DialogClose asChild>
                  <Button variant="outline">Abbrechen</Button>
                </DialogClose>
                <Button onClick={createBlockedSlot} variant="destructive">
                  Blockieren
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Pending Appointments Alert */}
      {stats.pending > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-yellow-800">
              <Clock className="h-5 w-5" />
              <span className="font-medium">{stats.pending} Termin(e) warten auf Bestätigung</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Appointments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Alle Termine ({appointments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {appointments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine Termine vorhanden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kunde</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Zeit</TableHead>
                    <TableHead>Art</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.map((apt) => (
                    <TableRow key={apt.id} className={apt.status === 'pending' ? 'bg-yellow-50' : ''}>
                      <TableCell>
                        <div className="font-medium">{apt.customer_name}</div>
                        <div className="text-sm text-muted-foreground">{apt.customer_email}</div>
                        {apt.customer_phone && (
                          <div className="text-sm text-muted-foreground">{apt.customer_phone}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {format(parseISO(apt.appointment_date), 'dd.MM.yyyy', { locale: de })}
                      </TableCell>
                      <TableCell>
                        {apt.start_time} - {apt.end_time}
                      </TableCell>
                      <TableCell>
                        {apt.appointment_type === 'remote' ? (
                          <span className="flex items-center gap-1 text-blue-600">
                            <Monitor className="h-4 w-4" /> Remote
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-teal-600">
                            <MapPin className="h-4 w-4" /> Vor Ort
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(apt.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {apt.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 h-8"
                                onClick={() => updateStatus(apt.id, 'confirmed')}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-8"
                                onClick={() => updateStatus(apt.id, 'cancelled')}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {apt.status === 'confirmed' && (
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 h-8"
                              onClick={() => updateStatus(apt.id, 'completed')}
                            >
                              Abschließen
                            </Button>
                          )}
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8"
                                onClick={() => setSelectedAppointment(apt)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                              <DialogHeader>
                                <DialogTitle>Termin-Details</DialogTitle>
                              </DialogHeader>
                              {selectedAppointment && (
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Kunde</label>
                                      <p className="font-medium">{selectedAppointment.customer_name}</p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Status</label>
                                      <p>{getStatusBadge(selectedAppointment.status)}</p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">E-Mail</label>
                                      <p>{selectedAppointment.customer_email}</p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Telefon</label>
                                      <p>{selectedAppointment.customer_phone || '-'}</p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Datum</label>
                                      <p>{format(parseISO(selectedAppointment.appointment_date), 'dd.MM.yyyy', { locale: de })}</p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Zeit</label>
                                      <p>{selectedAppointment.start_time} - {selectedAppointment.end_time}</p>
                                    </div>
                                  </div>
                                  {selectedAppointment.customer_message && (
                                    <div>
                                      <label className="text-sm font-medium text-muted-foreground">Kundennachricht</label>
                                      <p className="mt-1 p-2 bg-muted rounded text-sm">{selectedAppointment.customer_message}</p>
                                    </div>
                                  )}
                                  <div>
                                    <label className="text-sm font-medium text-muted-foreground">Admin-Notizen</label>
                                    <Textarea
                                      value={selectedAppointment.admin_notes || ''}
                                      onChange={(e) => setSelectedAppointment(prev => 
                                        prev ? { ...prev, admin_notes: e.target.value } : null
                                      )}
                                      placeholder="Interne Notizen..."
                                      rows={3}
                                      className="mt-1"
                                    />
                                    <Button
                                      size="sm"
                                      className="mt-2"
                                      onClick={() => {
                                        if (selectedAppointment) {
                                          updateAdminNotes(selectedAppointment.id, selectedAppointment.admin_notes || '');
                                        }
                                      }}
                                    >
                                      Notizen speichern
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-destructive hover:text-destructive"
                            onClick={() => deleteAppointment(apt.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blocked Slots */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5" />
            Blockierte Zeitslots ({blockedSlots.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blockedSlots.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">Keine blockierten Zeitslots</p>
          ) : (
            <div className="space-y-2">
              {blockedSlots.map((slot) => (
                <div key={slot.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {format(parseISO(slot.block_date), 'dd.MM.yyyy', { locale: de })}
                    </span>
                    <span>|</span>
                    <span>{slot.start_time} - {slot.end_time}</span>
                    {slot.reason && <span className="text-muted-foreground">({slot.reason})</span>}
                    {slot.is_from_google && (
                      <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                        Google
                      </Badge>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => deleteBlockedSlot(slot)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Google Calendar Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Google Calendar Verbindung
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {googleCalendarStatus.loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-600"></div>
              ) : googleCalendarStatus.connected ? (
                <>
                  <div className="flex items-center gap-2 text-green-600">
                    <Link2 className="h-5 w-5" />
                    <span className="font-medium">Verbunden</span>
                  </div>
                  <Badge className="bg-green-100 text-green-800">Aktiv</Badge>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Unlink className="h-5 w-5" />
                    <span>Nicht verbunden</span>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={checkGoogleCalendarStatus}
                disabled={googleCalendarStatus.loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${googleCalendarStatus.loading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
              {!googleCalendarStatus.connected && (
                <Button
                  onClick={connectGoogleCalendar}
                  disabled={connectingCalendar}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {connectingCalendar ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Verbinden...
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4 mr-2" />
                      Google Calendar verbinden
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          {googleCalendarStatus.connected && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium">Kalender auswählen:</label>
                {loadingCalendars ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-600"></div>
                ) : (
                  <Select 
                    value={selectedCalendarId} 
                    onValueChange={updateSelectedCalendar}
                  >
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Kalender wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {calendars.map((cal) => (
                        <SelectItem key={cal.id} value={cal.id}>
                          {cal.summary} {cal.primary && '(Hauptkalender)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Ihre Termine werden mit "{calendars.find(c => c.id === selectedCalendarId)?.summary || selectedCalendarId}" synchronisiert.
              </p>
            </div>
          )}
          {!googleCalendarStatus.connected && !googleCalendarStatus.loading && (
            <p className="text-sm text-muted-foreground mt-3">
              Verbinden Sie Ihren Google Calendar, um Termine automatisch zu synchronisieren.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
